import { cn } from "@/lib/cn";
import { shimmer, skeletonSurface } from "@/lib/shimmer";

/**
 * Rectangular chart placeholder with a subtle serpentine SVG path that
 * drifts horizontally. Height is tuned to match the Pulse line-charts.
 */
export function ChartSkeleton({
  height = 220,
  className,
}: {
  height?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[20px] border border-white/5 p-5",
        skeletonSurface,
        shimmer,
        className,
      )}
      style={{ height }}
      aria-hidden="true"
    >
      {/* Title line */}
      <div className="mb-4 flex items-center justify-between">
        <div className="h-3 w-40 rounded bg-white/[0.08]" />
        <div className="h-3 w-20 rounded bg-white/[0.04]" />
      </div>

      {/* Chart area */}
      <div className="relative h-[calc(100%-2.5rem)] w-full">
        <svg
          viewBox="0 0 400 120"
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          style={{
            animation: "skeleton-line-drift 4s ease-in-out infinite",
          }}
        >
          <defs>
            <linearGradient id="chart-skel-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(167,139,250,0.25)" />
              <stop offset="100%" stopColor="rgba(167,139,250,0)" />
            </linearGradient>
            <linearGradient id="chart-skel-stroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(192,132,252,0.6)" />
              <stop offset="100%" stopColor="rgba(236,72,153,0.6)" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[30, 60, 90].map((y) => (
            <line
              key={y}
              x1="0"
              x2="400"
              y1={y}
              y2={y}
              stroke="rgba(255,255,255,0.04)"
              strokeDasharray="2 6"
            />
          ))}

          {/* Area fill under the curve */}
          <path
            d="M0 80 C 40 60, 80 95, 120 70 S 200 40, 240 55 S 320 85, 400 60 L 400 120 L 0 120 Z"
            fill="url(#chart-skel-fill)"
          />
          {/* Curve */}
          <path
            d="M0 80 C 40 60, 80 95, 120 70 S 200 40, 240 55 S 320 85, 400 60"
            fill="none"
            stroke="url(#chart-skel-stroke)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    </div>
  );
}
